import { useContentStore } from "../../stores/content";
import {
  ComponentObject,
  CrossLayerConnection,
  PayloadLayer
} from "../typeDefinations";
import ModuleTemplate from "../modules/moduleTemplate";
import {
  PayloadChannel,
  PayloadPolygon,
  Point,
  PayloadLine,
  PayloadCurve
} from "@/library/payloadPrimitives";
import { Channel } from "@/library/channel";
import { LevelConnection } from "@/library/levelConnection";
import { InletOutlet } from "@/library/inletOutlet";
import { Bridge } from "@/library/bridge";
import ComponentPolygon from "@/library/componentPolygon";
import ComponentCircle from "@/library/componentCircle";
import { LocalCompRegistry } from '@/library/localCompRegistry';
import { VoronoiCompensationRegistry } from '@/services/voronoiCompensationRegistry';

// 判断一个 CurveShape 是否属于桥接结构（有明显的 Z 变化）
function isBridgeCurve(curve: PayloadCurve): boolean {
  const { start, end, center } = curve;
  const d1 = Math.abs(start.z - end.z);
  const d2 = Math.abs(start.z - center.z);
  const d3 = Math.abs(end.z   - center.z);
  return Math.max(d1, d2, d3) > 1e-6;
}

const globalCompensationPolygon = (
  h: number,
  elevation: number
): PayloadPolygon => {
  const z = elevation - h;
  const contentStore = useContentStore();
  const x = contentStore.chipLengthX;
  const y = contentStore.chipLengthY;
  const p1 = new Point(0, 0, z);
  const p2 = new Point(x, 0, z);
  const p3 = new Point(x, y, z);
  const p4 = new Point(0, y, z);
  return new PayloadPolygon([p1, p2, p3, p4], false, 0, 0, h);
};
const calculateLocalCompensation = (
  z: number,
  localCompMin: number,
  minAt: number,
  localCompMax: number,
  maxAt: number
): number => {
  if (localCompMin === 0) {
    localCompMin = 10 ** -9;
  }
  try {
    const maxmin = localCompMax / localCompMin;
    return (
      localCompMax +
      localCompMin -
      localCompMin * maxmin ** ((maxAt - z) / (maxAt - minAt))
    );
  } catch (err) {
    console.log(err);
    return 0;
  }
};

export const buildRequestBodyObject = (
  precision: string,
  globalCompCheck: boolean,
  globalComp: number,
  localCompCheck: boolean,
  localCompMin: number,
  minAt: number,
  localCompMax: number,
  maxAt: number
) => {
  //LocalCompRegistry.clear()

  const contentStore = useContentStore();
  const requestBody = {
    layers: Array<PayloadLayer>(),
    crosslayerConnections: Array<CrossLayerConnection>(),
    // {id:string, shapes: [connection (+ chamfer)]}
    relation: [],
    general: {
      length: contentStore.chipProperties[0].value,
      width: contentStore.chipProperties[1].value,
      thickness: contentStore.chipProperties[2].value,
      precision: precision
    }
  };
  if (globalCompCheck) {
    requestBody.general.thickness += globalComp * contentStore.layers.size;
  } else {
    globalComp = 0;
  }
  // sort layers
  const layersArr = Array.from(contentStore.layers.values());
  layersArr.sort((l1, l2) => {
    return l1.height - l2.height;
  });

  layersArr.forEach((layer, idx) => {
    const components = Array<ComponentObject>();
    const channels = Array<PayloadChannel>();

    const elevation = layer.height + globalComp * (idx + 1);
    const localComp = localCompCheck
      ? calculateLocalCompensation(
          elevation,
          localCompMin,
          minAt,
          localCompMax,
          maxAt
        )
      : 0;
    LocalCompRegistry.set(elevation, localComp);

    // reset channel points
    layer.contentIds.forEach((id) => {
      const elm = contentStore.getElement(id);
      if (elm instanceof Channel) {
        elm.resetBridges();
      }
    });
    // bind bridges to channel points
    layer.contentIds.forEach((id) => {
      const elm = contentStore.getElement(id);
      if (elm.display && elm instanceof Bridge) {
        elm.channelSegment.start.bridges.push(elm);
      }
    });
    // build
    layer.contentIds.forEach((id) => {
      const elm = contentStore.getElement(id);
      if (elm.display) {
        if (
          elm instanceof ModuleTemplate ||
          elm instanceof ComponentPolygon ||
          elm instanceof ComponentCircle
        ) {
          const p = elm.buildPayload(elevation - localComp, localComp);
          if (p) components.push(p);

        } else if (elm instanceof Channel) {
          // 默认参数
          const payloadDef = elm.buildPayload(elevation - localComp, localComp);
          // override 参数

          let avgComp=VoronoiCompensationRegistry.getAverage(elevation);
          if(avgComp){
            avgComp+=localComp;
          }else{
            avgComp = localComp;
          }
          const payloadOvr = elm.buildPayload(elevation - avgComp, avgComp);

          // 1) 默认里：保留水平 Line，以及非桥 Curve，和所有其它类型
          const keep = payloadDef.shapes.filter(shape => {
            if (shape instanceof PayloadLine && !(shape instanceof PayloadCurve)) {
              // 普通直线：水平才保留
              return Math.abs(shape.start.z - shape.end.z) < 1e-6;
            }
            if (shape instanceof PayloadCurve) {
              // 曲线：只有非桥结构才保留
              return !isBridgeCurve(shape);
            }
            return true;
          });
          // 2) override 里：保留非水平 Line，以及桥结构 Curve
          const take = payloadOvr.shapes.filter(shape => {
            if (shape instanceof PayloadLine && !(shape instanceof PayloadCurve)) {
              return Math.abs(shape.start.z - shape.end.z) >= 1e-6;
            }
            if (shape instanceof PayloadCurve) {
              return isBridgeCurve(shape);
            }
            return false;
          });
          // 合并并 push
          channels.push(new PayloadChannel([...keep, ...take]));
        }

        else if (elm instanceof InletOutlet) {
          const connection = elm.buildPayload(
            elevation - localComp,
            requestBody.general.thickness
          );
          if (connection) requestBody.crosslayerConnections.push(connection);
        }
      }
    });
    const newLayer: PayloadLayer = {
      elevation: elevation,
      components: components,
      channels: channels
    };
    if (globalCompCheck) {
      newLayer.compensation = globalCompensationPolygon(
        globalComp,
        elevation - localComp
      );
    }
    requestBody.layers.push(newLayer);
  });
  // build level connection
  contentStore.levelConnectionIds.forEach((id) => {
    const elm = contentStore.getElement(id);
    if (elm instanceof LevelConnection && elm.display) {
      const p = elm.buildPayload((x: number) => {
        if (localCompCheck)
          return calculateLocalCompensation(
            x,
            localCompMin,
            minAt,
            localCompMax,
            maxAt
          );
        else return 0;
      });

      if (p) {
        requestBody.crosslayerConnections.push(p);
      }
    }
  });
  // download for testing
  // const textBlob = new Blob([JSON.stringify(requestBody)], {
  //   type: "text/plain"
  // });
  // const a = document.createElement("a");
  // a.download = `requestBody.json`;
  // a.href = window.URL.createObjectURL(textBlob);
  // a.click();

  return requestBody;
};

export const buildRequestBody = (
  precision: string,
  globalCompCheck: boolean,
  globalComp: number,
  localCompCheck: boolean,
  localCompMin: number,
  minAt: number,
  localCompMax: number,
  maxAt: number
): string => {
  return JSON.stringify(buildRequestBodyObject(
    precision, globalCompCheck, globalComp,
    localCompCheck, localCompMin, minAt, localCompMax, maxAt
  ));
};
